import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useAssessmentSubmissionsQuery, useAssessmentsKeysQuery, useFormDefinitionQuery } from '@uplift/queries';
import { AssessmentKeys } from '@uplift/types';
import { ProgressChartHeaderVertical } from '@uplift/ui-v2';


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr',
  },
  chartItemColumn: {
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  }
}));


const AssessmentProgress: React.VFC<{
  patientId: string;
  assessmentId: string;
  assessmentKey: AssessmentKeys;
}> = ({
  patientId,
  assessmentId,
  assessmentKey,
}) => {
  const { data: formDefinition } = useFormDefinitionQuery(assessmentId, { suspense: true });
  const { data: submissions } = useAssessmentSubmissionsQuery(patientId, assessmentKey, { suspense: true });

  const lastSubmissions = React.useMemo(() => {
    return submissions?.find(s => s.submission.completedAt);
  }, [ submissions ]);

  return (
    <ProgressChartHeaderVertical
      title={formDefinition.name}
      value={lastSubmissions?.score ?? '–'}
      difference={lastSubmissions?.scoreDiffPercentage}
    />
  );
};

export type CompactAssessmentsProps = {
  patientId: string;
};

export const CompactAssessments: React.VFC<CompactAssessmentsProps> = ({ patientId }) => {
  const { classes } = useStyles();
  const { data } = useAssessmentsKeysQuery({ suspense: true });

  return (
    <Box className={classes.root}>
      {(Object.keys(data ?? {}) as (keyof typeof data)[]).map(assessmentKey => (
        <Box key={assessmentKey} className={classes.chartItemColumn}>
          <AssessmentProgress
            patientId={patientId}
            assessmentId={data[assessmentKey]}
            assessmentKey={assessmentKey}
          />
        </Box>
      ))}
    </Box>
  );
};
